import { lazy, Suspense } from 'react';

const Accordion = lazy(() => import('@/components/Accordion/Accordion'));
const AnnouncementButton = lazy(() => import('@/components/AnnouncementButton/AnnouncementButton'));
const ArticlePost = lazy(() => import('@/components/ArticlePost/ArticlePost'));
const CTABlock = lazy(() => import('@/components/CTABlock/CTABlock'));
const CardsBlock = lazy(() => import('@/components/CardsBlock/CardsBlock'));
const CarouselSection = lazy(() => import('@/components/CarouselSection/CarouselSection'));
const ColumnBlock = lazy(() => import('@/components/ColumnBlock/ColumnBlock'));
const ComboBlock = lazy(() => import('@/components/ComboBlock/ComboBlock'));
const ComparisonTable = lazy(() => import('@/components/ComparisonTable/ComparisonTable'));
const EditionsDownloadBlock = lazy(
  () => import('@/components/EditionsDownloadBlock/EditionsDownloadBlock')
);
const Footer = lazy(() => import('@/components/Footer/Footer'));
const FormContainer = lazy(() => import('@/components/FormContainer/FormContainer'));
const MidPageFormContainer = lazy(
  () => import('@/components/MidPageFormContainer/MidPageFormContainer')
);
const Header = lazy(() => import('@/components/Header/Header'));
const HeroBlockArticleDetail1 = lazy(
  () => import('@/components/HeroBlockArticleDetail1/HeroBlockArticleDetail1')
);
const HeroBlockArticleDetail2 = lazy(
  () => import('@/components/HeroBlockArticleDetail2/HeroBlockArticleDetail2')
);
const HeroBlockHomepage = lazy(() => import('@/components/HeroBlockHomepage/HeroBlockHomepage'));
const HeroBlockIndex = lazy(() => import('@/components/HeroBlockIndex/HeroBlockIndex'));
const HeroEmailForm = lazy(() => import('@/components/Forms/HeroEmailForm/HeroEmailForm'));
const Html = lazy(() => import('@/components/Html/Html'));
const Iframe = lazy(() => import('@/components/Iframe/Iframe'));
const ImageColumnBlock = lazy(() => import('@/components/ImageColumnBlock/ImageColumnBlock'));
const IndustryLogoBlock = lazy(() => import('@/components/IndustryLogoBlock/IndustryLogoBlock'));
const InfoTag = lazy(() => import('@/components/InfoTag/InfoTag'));
const Interstitial = lazy(() => import('@/components/Interstitial/Interstitial'));
const JumpToMenu = lazy(() => import('@/components/JumpToMenu/JumpToMenu'));
const LanguageGridBlock = lazy(() => import('@/components/LanguageGridBlock/LanguageGridBlock'));
const LanguageGridBlockV2 = lazy(
  () => import('@/components/LanguageGridBlockV2/LanguageGridBlockV2')
);
const LoginSection = lazy(() => import('@/components/LoginSection/LoginSection'));
const LogoBlock = lazy(() => import('@/components/LogoBlock/LogoBlock'));
const MediaCarousel = lazy(() => import('@/components/MediaCarousel/MediaCarousel'));
const MidPageCTA = lazy(() => import('@/components/MidPageCTA/MidPageCTA'));
const MidPageSearch = lazy(() => import('@/components/MidPageSearch/MidPageSearch'));
const Modal = lazy(() => import('@/components/Modal/Modal'));
const N5050Block = lazy(() => import('@/components/N5050Block/N5050Block'));
const N5050CardBlock = lazy(() => import('@/components/N5050CardBlock/N5050CardBlock'));
const OpenSourceSolutions = lazy(
  () => import('@/components/OpenSourceSolutions/OpenSourceSolutions')
);
const PaginatedSection = lazy(() => import('@/components/PaginatedSection/PaginatedSection'));
const PostGridBlock = lazy(() => import('@/components/PostGridBlock/PostGridBlock'));
const PricingSlider = lazy(() => import('@/components/PricingSlider/PricingSlider'));
const PricingV2Container = lazy(() => import('@/components/PricingV2/PricingContainer'));
const QuoteBlock = lazy(() => import('@/components/QuoteBlock/QuoteBlock'));
const QuoteBlockV2 = lazy(() => import('@/components/QuoteBlockV2/QuoteBlockV2'));
const ReleaseDownload = lazy(() => import('@/components/ReleaseDownload/ReleaseDownload'));
const SearchBlock = lazy(() => import('@/components/SearchBlock/SearchBlock'));
const SecondaryHeroBlock = lazy(() => import('@/components/SecondaryHeroBlock/SecondaryHeroBlock'));
const SecondaryNavigation = lazy(
  () => import('@/components/SecondaryNavigation/SecondaryNavigation')
);
const SectionIntroduction = lazy(
  () => import('@/components/SectionIntroduction/SectionIntroduction')
);
const SingleMedia = lazy(() => import('@/components/SingleMedia/SingleMedia'));
const StatBarBlock = lazy(() => import('@/components/StatBarBlock/StatBarBlock'));
const StickySidebar = lazy(() => import('@/components/StickySidebar/StickySidebar'));
const SuperCTABlock = lazy(() => import('@/components/SuperCTABlock/SuperCTABlock'));
const TabbedBlock = lazy(() => import('@/components/TabbedBlock/TabbedBlock'));
const TertiaryNavigation = lazy(() => import('@/components/TertiaryNavigation/TertiaryNavigation'));
const TestimonialBlock = lazy(() => import('@/components/TestimonialBlock/TestimonialBlock'));
const TimelineBlock = lazy(() => import('@/components/TimelineBlock/TimelineBlock'));
const VulnerabilityDisclosureTable = lazy(
  () => import('@/components/VulnerabilityDisclosureTable/VulnerabilityDisclosureTable')
);
const ZoomWebinarsContainer = lazy(() => import('@/components/ZoomWebinar/ZoomWebinarsContainer'));

export type ValidBlockType =
  | 'accordion'
  | 'announcement_button'
  | 'article_post'
  | 'building_block_wrapper'
  | 'cards_block'
  | 'carousel_section'
  | 'column_block'
  | 'combo_block'
  | 'comparison_table'
  | 'cta_block'
  | 'ctacard'
  | 'editions_download_block'
  | 'footer'
  | 'form_container'
  | 'header'
  | 'hero_block_article_detail_1'
  | 'hero_block_article_detail_2'
  | 'hero_block_homepage'
  | 'hero_block_index'
  | 'hero_email_form'
  | 'html'
  | 'iframe'
  | 'image_column_block'
  | 'industry_logo_block'
  | 'info_tag'
  | 'interstitial'
  | 'jump_to_menu'
  | 'language_grid_block'
  | 'login_section'
  | 'logo_block'
  | 'lts_download_container'
  | 'media_carousel'
  | 'midpage_form_container'
  | 'mid_page_search'
  | 'n5050_block'
  | 'n5050_card_block'
  | 'opensource_solutions'
  | 'paginated_section'
  | 'pricing_slider'
  | 'pricing_v2_container'
  | 'quote_block'
  | 'quote_block_v2'
  | 'release_download'
  | 'search_block'
  | 'secondary_hero_block'
  | 'secondary_navigation'
  | 'section_introduction'
  | 'single_media'
  | 'statbar_block'
  | 'super_cta_block'
  | 'tabbed_block'
  | 'testimonial_block'
  | 'timeline_block'
  | 'vulnerability_disclosure_table'
  | 'sticky_sidebar'
  | 'zoom_webinars_container';

export type Block = {
  system: {
    codename: string;
    /** GUID */
    id: string;
    type: ValidBlockType;
  };
  elements: any;
};

export type BlockHTMLId = { id?: HTMLElement['id'] };
export type BlockHTMLClassName = { className?: HTMLElement['className'] };

export type ComponentProps = {
  building_blocks?: any;
  block: Block;
  metaKeywords?: Queries.SEO_METAFragment['page_seo_snippet__meta_keywords'];
  data?: any;
  pageContext: any;
  creation_date?: {
    value: string;
  };
  footerProps: {
    layout?: Queries.PAGE_LAYOUTFragment['page_layout__footer'];
  };
  headerProps: {
    layout?: Queries.PAGE_LAYOUTFragment['page_layout__header'];
    isSticky?: boolean;
    hasCTAs?: boolean;
  };
} & BlockHTMLId;

const defaultTransform = ({ block }: ComponentProps) => block;

export default function renderBlock(props: ComponentProps) {
  const { type, id } = props.block.system;

  if (type === 'building_block_wrapper') {
    return renderBlock({
      ...props,
      block: props.block.elements.building_block.value,
      id: props.block.elements.anchor_id.value,
    });
  }

  const details = components.find((item) => item.type === type);

  if (!details) {
    return null;
  }

  const Component = details.component;
  const transformProps = details.transformProps || defaultTransform;

  return (
    <Suspense key={`${id}${props.id}`}>
      <Component {...transformProps(props)} id={props.id} />
    </Suspense>
  );
}

const components = [
  {
    type: 'announcement_button',
    component: AnnouncementButton,
  },
  {
    type: 'secondary_hero_block',
    component: SecondaryHeroBlock,
  },
  {
    type: 'logo_block',
    component: LogoBlock,
  },
  {
    type: 'n5050_block',
    component: N5050Block,
  },
  {
    type: 'column_block',
    component: ColumnBlock,
  },
  {
    type: 'article_post',
    component: ArticlePost,
  },
  {
    type: 'accordion',
    component: Accordion,
  },
  {
    type: 'testimonial_block',
    component: TestimonialBlock,
  },
  {
    type: 'cards_block',
    component: CardsBlock,
  },
  {
    type: 'image_column_block',
    component: ImageColumnBlock,
  },
  {
    type: 'language_grid_block',
    component: LanguageGridBlock,
  },
  {
    type: 'language_grid_block_v2',
    component: LanguageGridBlockV2,
  },
  {
    type: 'cta_block',
    component: CTABlock,
  },
  {
    type: 'statbar_block',
    component: StatBarBlock,
  },
  {
    type: 'secondary_navigation',
    component: SecondaryNavigation,
    transformProps: ({ block, pageContext }: ComponentProps) => ({
      ...block,
      pageContext,
    }),
  },
  {
    type: 'carousel_section',
    component: CarouselSection,
  },
  {
    type: 'paginated_section',
    component: PaginatedSection,
    transformProps: ({ pageContext, block }: ComponentProps) => ({
      ...block,
      ...pageContext,
    }),
  },
  {
    type: 'hero_block_article_detail_1',
    component: HeroBlockArticleDetail1,
    transformProps: ({ block, creation_date }: ComponentProps) => ({
      ...block,
      creationDate: creation_date?.value,
    }),
  },
  {
    type: 'hero_block_article_detail_2',
    component: HeroBlockArticleDetail2,
    transformProps: ({ block, creation_date, metaKeywords }: ComponentProps) => {
      return {
        ...block,
        creationDate: creation_date?.value,
        tags: (metaKeywords?.value ?? []).filter(
          (keyword): keyword is Queries.BLOG_TAGFragment =>
            keyword.__typename === 'kontent_item_blog_tag'
        ),
      };
    },
  },
  {
    type: 'hero_block_homepage',
    component: HeroBlockHomepage,
  },
  {
    type: 'hero_block_index',
    component: HeroBlockIndex,
  },
  {
    type: 'html',
    component: Html,
  },
  {
    type: 'industry_logo_block',
    component: IndustryLogoBlock,
  },
  {
    type: 'mid_page_search',
    component: MidPageSearch,
  },
  {
    type: 'opensource_solutions',
    component: OpenSourceSolutions,
  },
  {
    type: 'vulnerability_disclosure_table',
    component: VulnerabilityDisclosureTable,
  },
  {
    type: 'timeline_block',
    component: TimelineBlock,
  },
  {
    type: 'post_grid_block',
    component: PostGridBlock,
    transformProps: ({ pageContext, data, building_blocks }: ComponentProps) => ({
      pageInfo: data.allKontentItemGenericPostPage.pageInfo,
      paginationBaseURL: pageContext.paginationBaseURL,
      posts: data.allKontentItemGenericPostPage.nodes,
      cta: building_blocks?.value?.find((item: Block) => item.system.type === 'ctacard') ?? null,
    }),
  },
  {
    type: 'tabbed_block',
    component: TabbedBlock,
  },
  {
    type: 'tertiary_navigation',
    component: TertiaryNavigation,
  },
  {
    type: 'iframe',
    component: Iframe,
  },
  {
    type: 'login_section',
    component: LoginSection,
  },
  {
    type: 'editions_download_block',
    component: EditionsDownloadBlock,
  },
  {
    type: 'release_download',
    component: ReleaseDownload,
  },
  {
    type: 'mid_page_cta',
    component: MidPageCTA,
  },
  {
    type: 'modal',
    component: Modal,
  },
  {
    type: 'search_block',
    component: SearchBlock,
  },
  {
    type: 'n5050_card_block',
    component: N5050CardBlock,
  },
  {
    type: 'interstitial',
    component: Interstitial,
  },
  {
    type: 'info_tag',
    component: InfoTag,
  },
  {
    type: 'quote_block',
    component: QuoteBlock,
  },
  {
    type: 'section_introduction',
    component: SectionIntroduction,
  },
  {
    type: 'single_media',
    component: SingleMedia,
  },
  {
    type: 'combo_block',
    component: ComboBlock,
  },
  {
    type: 'jump_to_menu',
    component: JumpToMenu,
  },
  {
    type: 'media_carousel',
    component: MediaCarousel,
  },
  {
    type: 'comparison_table',
    component: ComparisonTable,
  },
  {
    type: 'header',
    component: Header,
    transformProps: ({ block, headerProps }: ComponentProps) => {
      return {
        ...block,
        ...headerProps,
      };
    },
  },
  {
    type: 'footer',
    component: Footer,
    transformProps: ({ block, footerProps }: ComponentProps) => {
      return {
        ...block,
        ...footerProps,
      };
    },
  },
  {
    type: 'pricing_slider_container',
    component: PricingSlider,
  },
  {
    type: 'pricing_v2_container',
    component: PricingV2Container,
  },
  {
    type: 'quote_block_v2',
    component: QuoteBlockV2,
  },
  {
    type: 'sticky_sidebar',
    component: StickySidebar,
  },
  {
    type: 'zoom_webinars_container',
    component: ZoomWebinarsContainer,
    transformProps: ({ pageContext }: ComponentProps) => {
      return {
        webinars: pageContext.webinars,
      };
    },
  },
  {
    type: 'form_container',
    component: FormContainer,
  },
  {
    type: 'midpage_form_container',
    component: MidPageFormContainer,
  },
  {
    type: 'hero_email_form',
    component: HeroEmailForm,
  },
  {
    type: 'super_cta_block',
    component: SuperCTABlock,
  },
];
